import React, { useState, useMemo, useCallback, useRef } from 'react'
import {
  Alert,
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PeopleIcon from '@mui/icons-material/People'
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount'

import AppleLogo from 'assets/sso/Apple.svg'
import CleverLogo from 'assets/sso/Clever.svg'
import ClassLinkLogo from 'assets/sso/ClassLink.svg'
import GoogleLogo from 'assets/sso/Google.png'
import FacebookLogo from 'assets/sso/Facebook.png'
import RemoveConfirmDialog from 'common/components/RemoveConfirmDialog'
import UploadFileIcon from '@mui/icons-material/UploadFile'

import { TeacherInfo, TeacherRow } from '../types'
import { codesToRecord } from '../helpers'
import CollapsibleSection from './CollapsibleSection'
import DataGridTable from './DataGridTable'
import ImportTeachersModal from './ImportTeachersModal'
import LicenseModal from './LicenseModal'

const SSO_PROVIDERS: Record<string, { src?: string; label: string }> = {
  apple: { src: AppleLogo, label: 'Apple' },
  clever: { src: CleverLogo, label: 'Clever' },
  classlink: { src: ClassLinkLogo, label: 'ClassLink' },
  google: { src: GoogleLogo, label: 'Google' },
  facebook: { src: FacebookLogo, label: 'Facebook' },
  amazon: { label: 'Amazon' },
}

function getSsoProvider(row: TeacherRow): string | null {
  if (row.appleId) return 'apple'
  if (row.cleverId) return 'clever'
  return row.socialProvider ?? null
}

function SsoIcon({ row }: { row: TeacherRow }) {
  const key = getSsoProvider(row)
  if (!key) {
    return (
      <Typography variant='caption' color='text.secondary'>
        —
      </Typography>
    )
  }
  const provider = SSO_PROVIDERS[key] ?? { label: key }
  if (provider.src) {
    return (
      <Box
        component='img'
        src={provider.src}
        alt={provider.label}
        sx={{ height: 20, width: 'auto' }}
      />
    )
  }
  return (
    <Typography
      variant='caption'
      sx={
        provider.src === undefined && (key === 'apple' || key === 'amazon')
          ? { fontWeight: 600 }
          : { color: 'text.secondary' }
      }
    >
      {provider.label}
    </Typography>
  )
}

function makeTeacherColumns(parentOrigin: string): GridColDef[] {
  return [
    {
      field: 'code',
      headerName: 'Code',
      width: 140,
      filterable: true,
      renderCell: (params) => (
        <Button
          variant='text'
          size='small'
          onClick={() =>
            window.open(`${parentOrigin}/teacher?t=${params.value}`, '_blank')
          }
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: 'none',
            p: 0,
            minWidth: 'auto',
            '&:hover': {
              textDecoration: 'underline',
              backgroundColor: 'transparent',
            },
          }}
        >
          {params.value}
        </Button>
      ),
    },
    { field: 'name', headerName: 'Name', width: 200, filterable: true },
    { field: 'email', headerName: 'Email', width: 260, filterable: true },
    {
      field: 'sso',
      headerName: 'SSO',
      width: 80,
      sortable: false,
      filterable: true,
      valueGetter: (params) => {
        const key = getSsoProvider(params.row)
        return key ? SSO_PROVIDERS[key]?.label ?? key : ''
      },
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <SsoIcon row={params.row} />
        </Box>
      ),
    },
    {
      field: 'signIn',
      headerName: 'Sign In',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (params.row.missing) return null
        return (
          <Button
            variant='text'
            size='small'
            onClick={() =>
              window.open(
                `https://xtramath.org/#/signin/teacher?t=${params.row.code}&x=${params.row.secret}&a=1`,
                '_blank'
              )
            }
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: 'none',
              p: 0,
              minWidth: 'auto',
              '&:hover': {
                textDecoration: 'underline',
                backgroundColor: 'transparent',
              },
            }}
          >
            Sign In
          </Button>
        )
      },
    },
  ]
}

function toRows(teachers: TeacherInfo[]): TeacherRow[] {
  return teachers.map((t) => ({ id: t.code, ...t }))
}

interface UserManagementProps {
  admins: TeacherInfo[]
  teachers: TeacherInfo[]
  lCode: string
  parentOrigin: string
  actionLoading: boolean
  postAction: (action: string, body?: Record<string, unknown>) => void
  setError: (err: string | null) => void
}

function UserManagement({
  admins,
  teachers,
  lCode,
  parentOrigin,
  actionLoading,
  postAction,
  setError,
}: UserManagementProps) {
  const columns = useMemo(
    () => makeTeacherColumns(parentOrigin),
    [parentOrigin]
  )
  const adminRows = useMemo(() => toRows(admins), [admins])
  const teacherRows = useMemo(() => toRows(teachers), [teachers])

  const [adminSelection, setAdminSelection] = useState<GridRowSelectionModel>(
    []
  )
  const [teacherSelection, setTeacherSelection] =
    useState<GridRowSelectionModel>([])

  const [addAdminMode, setAddAdminMode] = useState<'email' | 'code'>('email')
  const [addAdminValue, setAddAdminValue] = useState('')
  const addAdminValueRef = useRef('')
  const [addTeacherMode, setAddTeacherMode] = useState<'email' | 'code'>(
    'email'
  )
  const [addTeacherValue, setAddTeacherValue] = useState('')
  const addTeacherValueRef = useRef('')
  const [addAdminsOpen, setAddAdminsOpen] = useState(false)
  const [addAdminsError, setAddAdminsError] = useState('')
  const [addTeachersOpen, setAddTeachersOpen] = useState(false)
  const [addTeachersError, setAddTeachersError] = useState('')
  const [removeAdminsConfirmOpen, setRemoveAdminsConfirmOpen] = useState(false)
  const [removeTeachersConfirmOpen, setRemoveTeachersConfirmOpen] =
    useState(false)
  const [importTeachersOpen, setImportTeachersOpen] = useState(false)

  const handleRemoveAdmins = async () => {
    try {
      await postAction('removeAdmins', {
        teachers: codesToRecord(adminSelection),
      })
      setAdminSelection([])
    } catch {
      /* error shown via hook */
    }
  }

  const handleRemoveTeachers = async () => {
    try {
      await postAction('removeTeachers', {
        teachers: codesToRecord(teacherSelection),
      })
      setTeacherSelection([])
    } catch {
      /* error shown via hook */
    }
  }

  const handleAddAdmins = async () => {
    setAddAdminsError('')
    try {
      const val = encodeURIComponent(addAdminValueRef.current.trim())
      const codes = addAdminMode === 'code' ? val : ''
      const emails = addAdminMode === 'email' ? val : ''
      await postAction('addAdmins', { tCodes: codes, tEmails: emails })
      addAdminValueRef.current = ''
      setAddAdminValue('')
      setAddAdminsOpen(false)
    } catch (err: unknown) {
      setError(null)
      const msg =
        (err as { response?: { data?: { error?: string } } }).response?.data
          ?.error ||
        (err as { response?: { data?: string } }).response?.data ||
        (err as { message?: string }).message ||
        'Failed to add admins'
      setAddAdminsError(typeof msg === 'string' ? msg : 'Failed to add admins')
    }
  }

  const handleAddTeachers = async () => {
    setAddTeachersError('')
    try {
      console.log('addTeacherValueRef.current', addTeacherValueRef.current)
      const tVal = encodeURIComponent(addTeacherValueRef.current.trim())
      const codes = addTeacherMode === 'code' ? tVal : ''
      const emails = addTeacherMode === 'email' ? tVal : ''
      await postAction('addTeachers', { tCodes: codes, tEmails: emails })
      addTeacherValueRef.current = ''
      setAddTeacherValue('')
      setAddTeachersOpen(false)
    } catch (err: unknown) {
      setError(null)
      const msg =
        (err as { response?: { data?: { error?: string } } }).response?.data
          ?.error ||
        (err as { response?: { data?: string } }).response?.data ||
        (err as { message?: string }).message ||
        'Failed to add teachers'
      setAddTeachersError(
        typeof msg === 'string' ? msg : 'Failed to add teachers'
      )
    }
  }

  const handleImportTeachers = useCallback(
    async (name: string[], email: string[]) => {
      await postAction('upsertTeachers', { name, email, license: lCode })
    },
    [postAction, lCode]
  )

  return (
    <Box>
      <Typography
        variant='h5'
        sx={{
          fontWeight: 600,
          mb: 2,
          borderLeft: '4px solid',
          borderColor: 'primary.main',
          pl: 2,
          py: 0.5,
        }}
      >
        User Management
      </Typography>

      {/* Admins */}
      <CollapsibleSection
        title='Admins'
        count={admins.length}
        icon={SupervisorAccountIcon}
        onAdd={() => setAddAdminsOpen(true)}
        onDeleteSelected={() => setRemoveAdminsConfirmOpen(true)}
        deleteDisabled={adminSelection.length === 0}
        deleteCount={adminSelection.length}
      >
        {adminRows.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            There are no admins.
          </Typography>
        ) : (
          <DataGridTable
            rows={adminRows}
            columns={columns}
            checkboxSelection
            selectionModel={adminSelection}
            onSelectionModelChange={setAdminSelection}
          />
        )}
      </CollapsibleSection>

      <LicenseModal
        open={addAdminsOpen}
        onClose={() => {
          setAddAdminsOpen(false)
          setAddAdminsError('')
        }}
        title='Add Admins'
        primaryLabel='Add Admins'
        onPrimary={handleAddAdmins}
        primaryDisabled={actionLoading || !addAdminValue.trim()}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          {addAdminsError && (
            <Alert severity='error' onClose={() => setAddAdminsError('')}>
              {addAdminsError}
            </Alert>
          )}
          <ToggleButtonGroup
            value={addAdminMode}
            exclusive
            onChange={(_, v) => v && setAddAdminMode(v)}
            fullWidth
            size='small'
            color='primary'
          >
            <ToggleButton value='email'>Email</ToggleButton>
            <ToggleButton value='code'>Code</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size='small'
            label={
              addAdminMode === 'email'
                ? 'Teacher emails (one per line)'
                : 'Teacher codes (one per line)'
            }
            multiline
            minRows={4}
            value={addAdminValue}
            onChange={(e) => {
              addAdminValueRef.current = e.target.value
              setAddAdminValue(e.target.value)
              setAddAdminsError('')
            }}
            fullWidth
            placeholder='Please write each on a separate line.'
          />
        </Stack>
      </LicenseModal>

      <RemoveConfirmDialog
        open={removeAdminsConfirmOpen}
        onClose={() => setRemoveAdminsConfirmOpen(false)}
        onConfirm={handleRemoveAdmins}
        title='Remove Admin'
        disabled={actionLoading}
      />

      {/* Teachers */}
      <CollapsibleSection
        title='Teachers'
        count={teachers.length}
        icon={PeopleIcon}
        actions={
          <Tooltip title='Import data'>
            <span>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation()
                  setImportTeachersOpen(true)
                }}
                disabled={actionLoading}
                aria-label='Import data'
              >
                <UploadFileIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
        onAdd={() => setAddTeachersOpen(true)}
        onDeleteSelected={() => setRemoveTeachersConfirmOpen(true)}
        deleteDisabled={teacherSelection.length === 0}
        deleteCount={teacherSelection.length}
      >
        {teacherRows.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            There are no teachers.
          </Typography>
        ) : (
          <DataGridTable
            rows={teacherRows}
            columns={columns}
            checkboxSelection
            selectionModel={teacherSelection}
            onSelectionModelChange={setTeacherSelection}
            pageSize={100}
          />
        )}
      </CollapsibleSection>

      <LicenseModal
        open={addTeachersOpen}
        onClose={() => {
          setAddTeachersOpen(false)
          setAddTeachersError('')
        }}
        title='Add Teachers'
        primaryLabel='Add Teachers'
        onPrimary={handleAddTeachers}
        primaryDisabled={actionLoading || !addTeacherValue.trim()}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          {addTeachersError && (
            <Alert severity='error' onClose={() => setAddTeachersError('')}>
              {addTeachersError}
            </Alert>
          )}
          <ToggleButtonGroup
            value={addTeacherMode}
            exclusive
            onChange={(_, v) => v && setAddTeacherMode(v)}
            fullWidth
            size='small'
            color='primary'
          >
            <ToggleButton value='email'>Email</ToggleButton>
            <ToggleButton value='code'>Code</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size='small'
            label={
              addTeacherMode === 'email'
                ? 'Teacher emails (one per line)'
                : 'Teacher codes (one per line)'
            }
            multiline
            minRows={4}
            value={addTeacherValue}
            onChange={(e) => {
              addTeacherValueRef.current = e.target.value
              setAddTeacherValue(e.target.value)
              setAddTeachersError('')
            }}
            fullWidth
            placeholder='Please write each on a separate line.'
          />
        </Stack>
      </LicenseModal>

      <RemoveConfirmDialog
        open={removeTeachersConfirmOpen}
        onClose={() => setRemoveTeachersConfirmOpen(false)}
        onConfirm={handleRemoveTeachers}
        title='Remove Teacher'
        disabled={actionLoading}
      />

      <ImportTeachersModal
        open={importTeachersOpen}
        onClose={() => setImportTeachersOpen(false)}
        onImport={handleImportTeachers}
        disabled={actionLoading}
      />
    </Box>
  )
}

export default React.memo(UserManagement)
